import {
  BoxGeometry,
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  type Object3D,
  Plane,
  Vector3
} from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { disposeObject3DResources as disposeMeshes } from "../models/shared-model-resources";
import { RackPostDirection } from "./rack-post";
import { RackPostRow } from "./rack-post-row";
import { RackShelfSideBrace } from "./rack-shelf-side-brace";

type RackShelfPosition = {
  x?: number;
  y?: number;
  z?: number;
};

export type RackShelfOptions = {
  bottomBraceY?: number;
  bottomBraceZ?: number;
  columnBraceXOffset?: number;
  crossbarZOffset?: number;
  height?: number;
  heightCellCount?: number;
  length?: number;
  lengthCellCount?: number;
  position?: RackShelfPosition;
  topBraceInset?: number;
  width?: number;
};

const defaultRackShelfOptions = {
  bottomBraceY: 0,
  columnBraceXOffset: 0.026,
  crossbarZOffset: 0.0185,
  height: 1,
  heightCellCount: 1,
  length: 1,
  lengthCellCount: 1,
  topBraceInset: 0.5,
  width: 1
};
const columnBraceLengthScale = 4;
const columnDiagonalBraceMaxAngle = Math.PI / 3;
const columnDiagonalBraceMinAngle = (36 * Math.PI) / 180;
const columnClipAreaXLength = 2;
const columnClipAreaXHalfLength = columnClipAreaXLength / 2;
const cargoCellHighlightColor = "#8b5cf6";
const cargoCellHighlightEdgeOpacity = 1;
const cargoCellHighlightOpacity = 0.58;
const cargoCellHighlightEdgeDashCount = 12;
const cargoCellHighlightEdgeDarkColor = [0, 0, 0] as const;
const cargoCellHighlightEdgeLightColor = [1, 1, 1] as const;
const cargoCellSelectionHighlightColor = "#c58d6b";
const cargoCellSelectionHighlightOpacity = 0.48;
const cargoCellSelectionHighlightScale = 0.94;
const rackSelectionHighlightColor = "#7895a6";
const rackSelectionHighlightOpacity = 0.3;
const rackSelectionHighlightPadding = 0.06;

type RackShelfColumnBrace = {
  bottomBrace: RackShelfSideBrace;
  clipPlanes: Plane[];
  diagonalBraces: RackShelfSideBrace[];
  root: Group;
  topBrace: RackShelfSideBrace;
  x: number;
};

type RackShelfColumnDiagonalBraceLayout = {
  angle: number;
  count: number;
  segmentHeight: number;
};

type RackShelfCargoCellHitTarget = Mesh<BoxGeometry, MeshBasicMaterial> & {
  userData: {
    rackShelf: RackShelf;
    rackShelfCargoCell: {
      heightIndex: number;
      lengthIndex: number;
    };
  };
};

export class RackShelf extends Group {
  private backRow?: RackPostRow;
  private bottomBraceY: number;
  private readonly cargoCellGroup = createCargoCellGroup();
  private readonly cargoCellHighlight = createCargoCellHighlight();
  private readonly cargoCellSelectionHighlight = createCargoCellSelectionHighlight();
  private readonly rackSelectionHighlight = createRackSelectionHighlight();
  private cargoCellHitTargets: RackShelfCargoCellHitTarget[] = [];
  private activeCargoCellHitTarget?: RackShelfCargoCellHitTarget;
  private selectedCargoCellHitTarget?: RackShelfCargoCellHitTarget;
  private selected = false;
  private bottomBraceZ?: number;
  private columnBraceXOffset: number;
  private crossbarZOffset: number;
  private frontRow?: RackPostRow;
  private height: number;
  private heightCellCount: number;
  private columnBraces: RackShelfColumnBrace[] = [];
  private length: number;
  private lengthCellCount: number;
  private topBraceInset: number;
  private width: number;

  constructor({
    bottomBraceY = defaultRackShelfOptions.bottomBraceY,
    bottomBraceZ,
    columnBraceXOffset = defaultRackShelfOptions.columnBraceXOffset,
    crossbarZOffset = defaultRackShelfOptions.crossbarZOffset,
    height = defaultRackShelfOptions.height,
    heightCellCount = defaultRackShelfOptions.heightCellCount,
    length = defaultRackShelfOptions.length,
    lengthCellCount = defaultRackShelfOptions.lengthCellCount,
    position = {},
    topBraceInset = defaultRackShelfOptions.topBraceInset,
    width = defaultRackShelfOptions.width
  }: RackShelfOptions = {}) {
    super();
    this.bottomBraceY = bottomBraceY;
    this.bottomBraceZ = bottomBraceZ;
    this.columnBraceXOffset = columnBraceXOffset;
    this.crossbarZOffset = crossbarZOffset;
    this.height = height;
    this.heightCellCount = heightCellCount;
    this.length = length;
    this.lengthCellCount = lengthCellCount;
    this.topBraceInset = topBraceInset;
    this.width = width;
    this.name = "rack-shelf";
    this.cargoCellGroup.add(this.cargoCellSelectionHighlight, this.cargoCellHighlight);
    this.add(this.cargoCellGroup, this.rackSelectionHighlight);
    this.setShelfPosition(position);
    this.updateRackSelectionHighlight();
    this.updateClipAreas();
  }

  static async create(
    options: RackShelfOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const shelf = new RackShelf(options);
    await shelf.load(loader);
    return shelf;
  }

  setCrossbarZOffset(crossbarZOffset: number) {
    this.crossbarZOffset = crossbarZOffset;
    this.frontRow?.setCrossbarZOffset(crossbarZOffset);
    this.backRow?.setCrossbarZOffset(crossbarZOffset);
  }

  setColumnBraceXOffset(columnBraceXOffset: number) {
    this.columnBraceXOffset = columnBraceXOffset;
    this.updateColumnBraceTransforms();
    this.updateClipPlanes();
  }

  async setBottomBracePosition(
    { y, z }: { y?: number; z?: number },
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const shouldReloadColumnBraces = y !== undefined && y !== this.bottomBraceY;

    this.bottomBraceY = y ?? this.bottomBraceY;
    this.bottomBraceZ = z ?? this.bottomBraceZ;

    if (shouldReloadColumnBraces) {
      await this.reloadColumnBraces(loader);
    } else {
      this.updateColumnBraceTransforms();
    }
  }

  async setTopBraceInset(topBraceInset: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.topBraceInset = topBraceInset;
    await this.reloadColumnBraces(loader);
  }

  async setHeight(height: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.height = height;
    await this.reloadColumnBraces(loader);
    this.updateClipAreas();
    this.updateCargoCellHitTargets();
    this.updateRackSelectionHighlight();
    await Promise.all([
      this.frontRow?.setTotalHeight(height, loader),
      this.backRow?.setTotalHeight(height, loader)
    ]);
  }

  async setHeightCellCount(heightCellCount: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.heightCellCount = heightCellCount;
    await Promise.all([
      this.frontRow?.setHeightCellCount(heightCellCount, loader),
      this.backRow?.setHeightCellCount(heightCellCount, loader)
    ]);
    this.reloadCargoCellHitTargets();
  }

  async setLength(length: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.length = length;
    this.frontRow?.setTotalLength(length);
    this.backRow?.setTotalLength(length);
    this.updateColumnBraceTransforms();
    this.updateClipAreas();
    this.updateCargoCellHitTargets();
    this.updateRackSelectionHighlight();
    await Promise.all([
      this.frontRow?.setLengthCellCount(this.lengthCellCount, loader),
      this.backRow?.setLengthCellCount(this.lengthCellCount, loader)
    ]);
  }

  async setLengthCellCount(lengthCellCount: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.lengthCellCount = lengthCellCount;
    await Promise.all([
      this.frontRow?.setLengthCellCount(lengthCellCount, loader),
      this.backRow?.setLengthCellCount(lengthCellCount, loader)
    ]);
    await this.reloadColumnBraces(loader);
    this.reloadCargoCellHitTargets();
  }

  setShelfPosition(position: RackShelfPosition) {
    this.position.set(
      position.x ?? this.position.x,
      position.y ?? this.position.y,
      position.z ?? this.position.z
    );
    this.updateClipPlanes();
  }

  async setWidth(width: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.width = width;
    this.updateRowPositions();
    await this.reloadColumnBraces(loader);
    this.updateClipAreas();
    this.updateCargoCellHitTargets();
    this.updateRackSelectionHighlight();
  }

  getCargoCellHitTargets() {
    return this.cargoCellHitTargets;
  }

  showCargoCellHighlight(hitTarget: Object3D) {
    if (!this.isCargoCellHitTarget(hitTarget)) {
      return;
    }

    this.activeCargoCellHitTarget = hitTarget;
    this.cargoCellHighlight.position.copy(hitTarget.position);
    this.cargoCellHighlight.scale.copy(hitTarget.scale);
    this.cargoCellHighlight.visible = true;
  }

  hideCargoCellHighlight() {
    this.activeCargoCellHitTarget = undefined;
    this.cargoCellHighlight.visible = false;
  }

  selectCargoCell(hitTarget: Object3D) {
    if (!this.isCargoCellHitTarget(hitTarget)) {
      return;
    }

    this.selectedCargoCellHitTarget = hitTarget;
    this.cargoCellSelectionHighlight.position.copy(hitTarget.position);
    this.cargoCellSelectionHighlight.scale.copy(hitTarget.scale);
    this.cargoCellSelectionHighlight.scale.multiplyScalar(cargoCellSelectionHighlightScale);
    this.cargoCellSelectionHighlight.visible = true;
  }

  clearCargoCellSelection() {
    this.selectedCargoCellHitTarget = undefined;
    this.cargoCellSelectionHighlight.visible = false;
  }

  setSelected(selected: boolean) {
    this.selected = selected;
    this.rackSelectionHighlight.visible = selected;

    if (selected) {
      this.updateRackSelectionHighlight();
    } else {
      this.clearCargoCellSelection();
    }
  }

  private async load(loader: ModelAssetLoader) {
    const [frontRow, backRow] = await Promise.all([
      this.createRow(RackPostDirection.Front, loader),
      this.createRow(RackPostDirection.Back, loader)
    ]);

    this.frontRow = frontRow;
    this.backRow = backRow;
    this.add(frontRow, backRow);
    this.updateRowPositions();
    await this.reloadColumnBraces(loader);
    this.reloadCargoCellHitTargets();
    this.updateRackSelectionHighlight();
    this.updateClipAreas();
  }

  private createRow(direction: RackPostDirection, loader: ModelAssetLoader) {
    return RackPostRow.create(
      {
        crossbarZOffset: this.crossbarZOffset,
        direction,
        heightCellCount: this.heightCellCount,
        lengthCellCount: this.lengthCellCount,
        totalHeight: this.height,
        totalLength: this.length
      },
      loader
    );
  }

  private createSideBrace(loader: ModelAssetLoader) {
    return RackShelfSideBrace.create(loader);
  }

  private async reloadColumnBraces(loader: ModelAssetLoader) {
    this.columnBraces.forEach(({ root }) => {
      this.remove(root);
      disposeMeshes(root);
    });
    this.columnBraces = [];

    const diagonalBraceCount =
      createColumnDiagonalBraceLayout(this.bottomBraceY, this.getTopBraceY(), this.width)?.count ??
      0;
    const postXPositions = createPostXPositions(this.length, this.lengthCellCount);
    const columnBraces = await Promise.all(
      postXPositions.map(async (x, index) => {
        const [bottomBrace, topBrace] = await Promise.all([
          this.createSideBrace(loader),
          this.createSideBrace(loader)
        ]);
        const diagonalBraces = await Promise.all(
          Array.from({ length: diagonalBraceCount }, () => this.createSideBrace(loader))
        );
        const clipPlanes = Array.from({ length: 6 }, () => new Plane());
        const root = createColumnBraceRoot(
          `rack-shelf-column-brace-root-${index}`,
          bottomBrace,
          topBrace,
          ...diagonalBraces
        );

        [bottomBrace, topBrace, ...diagonalBraces].forEach((brace) => {
          brace.setClippingPlanes(clipPlanes);
        });
        return { bottomBrace, clipPlanes, diagonalBraces, root, topBrace, x };
      })
    );

    this.columnBraces = columnBraces;
    this.add(...columnBraces.map(({ root }) => root));
    this.updateColumnBraceTransforms();
    this.updateClipAreas();
  }

  private reloadCargoCellHitTargets() {
    this.hideCargoCellHighlight();
    this.clearCargoCellSelection();
    this.cargoCellHitTargets.forEach((hitTarget) => {
      this.cargoCellGroup.remove(hitTarget);
      disposeMeshes(hitTarget);
    });
    this.cargoCellHitTargets = [];

    this.cargoCellHitTargets = Array.from(
      { length: this.lengthCellCount * this.heightCellCount },
      (_, index) => {
        const lengthIndex = index % this.lengthCellCount;
        const heightIndex = Math.floor(index / this.lengthCellCount);
        const hitTarget = createCargoCellHitTarget(this, lengthIndex, heightIndex);

        this.updateCargoCellHitTarget(hitTarget);
        return hitTarget;
      }
    );

    this.cargoCellGroup.add(...this.cargoCellHitTargets);
  }

  private updateCargoCellHitTargets() {
    this.cargoCellHitTargets.forEach((hitTarget) => {
      this.updateCargoCellHitTarget(hitTarget);
    });

    if (this.activeCargoCellHitTarget) {
      this.showCargoCellHighlight(this.activeCargoCellHitTarget);
    }

    if (this.selectedCargoCellHitTarget) {
      this.selectCargoCell(this.selectedCargoCellHitTarget);
    }
  }

  private updateCargoCellHitTarget(hitTarget: RackShelfCargoCellHitTarget) {
    const { heightIndex, lengthIndex } = hitTarget.userData.rackShelfCargoCell;
    const cellLength = this.length / this.lengthCellCount;
    const cellHeight = this.height / this.heightCellCount;

    hitTarget.position.set(
      cellLength * (lengthIndex + 0.5),
      cellHeight * (heightIndex + 0.5),
      this.width / 2
    );
    hitTarget.scale.set(cellLength, cellHeight, this.width);
  }

  private updateRackSelectionHighlight() {
    this.rackSelectionHighlight.position.set(this.length / 2, this.height / 2, this.width / 2);
    this.rackSelectionHighlight.scale.set(
      this.length + rackSelectionHighlightPadding * 2,
      this.height + rackSelectionHighlightPadding * 2,
      this.width + rackSelectionHighlightPadding * 2
    );
    this.rackSelectionHighlight.visible = this.selected;
  }

  private updateRowPositions() {
    this.backRow?.setRackRowPosition({ x: 0, y: 0, z: 0 });
    this.frontRow?.setRackRowPosition({ x: 0, y: 0, z: this.width });
  }

  private updateColumnBraceTransforms() {
    const postXPositions = createPostXPositions(this.length, this.lengthCellCount);

    this.columnBraces.forEach((columnBrace, index) => {
      columnBrace.x = postXPositions[index] ?? columnBrace.x;
      this.updateColumnBraceTransform(columnBrace);
    });
  }

  private updateColumnBraceTransform({
    bottomBrace,
    diagonalBraces,
    root,
    topBrace,
    x
  }: RackShelfColumnBrace) {
    const topBraceY = this.getTopBraceY();

    root.position.set(x + this.columnBraceXOffset, 0, this.bottomBraceZ ?? 0);
    root.rotation.y = 0;
    this.updateColumnBraceItem(bottomBrace, this.bottomBraceY);
    this.updateColumnBraceItem(topBrace, topBraceY);
    this.updateColumnDiagonalBraceItems(diagonalBraces, topBraceY);
  }

  private updateColumnBraceItem(brace: RackShelfSideBrace, y: number) {
    brace.position.set(0, y, 0);
    brace.rotation.set(0, 0, 0);
    brace.scale.set(1, 1, 1);
    brace.setLengthScale(columnBraceLengthScale);
  }

  private updateColumnDiagonalBraceItems(diagonalBraces: RackShelfSideBrace[], topBraceY: number) {
    const layout = createColumnDiagonalBraceLayout(this.bottomBraceY, topBraceY, this.width);

    diagonalBraces.forEach((brace, index) => {
      if (!layout || index >= layout.count) {
        brace.visible = false;
        return;
      }

      const isForward = index % 2 === 0;
      const y = this.bottomBraceY + layout.segmentHeight * index;
      const z = isForward ? 0 : this.width;
      const rotationX = isForward ? -layout.angle : Math.PI + layout.angle;

      brace.visible = true;
      brace.position.set(0, y, z);
      brace.rotation.set(rotationX, 0, 0);
      brace.scale.set(1, 1, 1);
      brace.setLengthScale(columnBraceLengthScale / Math.cos(layout.angle));
    });
  }

  private getTopBraceY() {
    return this.height - this.topBraceInset;
  }

  private updateClipAreas() {
    this.updateClipPlanes();
  }

  private updateClipPlanes() {
    this.updateWorldMatrix(true, true);

    this.columnBraces.forEach(({ clipPlanes, x }) => {
      setClipBoxPlanes(clipPlanes, this, x + this.columnBraceXOffset, this.height, this.width);
    });
  }

  private isCargoCellHitTarget(object: Object3D): object is RackShelfCargoCellHitTarget {
    return object.userData.rackShelf === this && object.userData.rackShelfCargoCell !== undefined;
  }
}

function createCargoCellGroup() {
  const group = new Group();
  group.name = "rack-shelf-cargo-cell-hit-targets";
  return group;
}

function createCargoCellHitTarget(
  shelf: RackShelf,
  lengthIndex: number,
  heightIndex: number
): RackShelfCargoCellHitTarget {
  const material = new MeshBasicMaterial({
    color: cargoCellHighlightColor,
    depthWrite: false,
    opacity: 0,
    transparent: true
  });
  material.colorWrite = false;

  const hitTarget = new Mesh(new BoxGeometry(1, 1, 1), material) as RackShelfCargoCellHitTarget;
  hitTarget.name = `rack-shelf-cargo-cell-${lengthIndex}-${heightIndex}`;
  hitTarget.userData.rackShelf = shelf;
  hitTarget.userData.rackShelfCargoCell = { heightIndex, lengthIndex };
  return hitTarget;
}

export function createCargoCellHighlight() {
  return createBoxHighlight({
    fillColor: cargoCellHighlightColor,
    fillOpacity: cargoCellHighlightOpacity,
    name: "rack-shelf-cargo-cell-highlight",
    renderOrder: 10,
    showEdges: false
  });
}

export function createCargoCellSelectionHighlight() {
  return createBoxHighlight({
    fillColor: cargoCellSelectionHighlightColor,
    fillOpacity: cargoCellSelectionHighlightOpacity,
    name: "rack-shelf-cargo-cell-selection-highlight",
    renderOrder: 11
  });
}

export function createRackSelectionHighlight() {
  return createBoxHighlight({
    fillColor: rackSelectionHighlightColor,
    fillOpacity: rackSelectionHighlightOpacity,
    name: "rack-shelf-selection-highlight",
    renderOrder: 7
  });
}

function createBoxHighlight({
  fillColor,
  fillOpacity,
  name,
  renderOrder,
  showEdges = true
}: {
  fillColor: string;
  fillOpacity: number;
  name: string;
  renderOrder: number;
  showEdges?: boolean;
}) {
  const highlight = new Group();
  const fill = new Mesh(
    new BoxGeometry(1, 1, 1),
    new MeshStandardMaterial({
      color: fillColor,
      depthWrite: false,
      emissive: fillColor,
      emissiveIntensity: 0.28,
      envMapIntensity: 0.15,
      metalness: 0.04,
      opacity: fillOpacity,
      roughness: 0.62,
      transparent: true
    })
  );

  highlight.name = name;
  fill.name = `${name}-fill`;
  fill.renderOrder = renderOrder;
  highlight.add(fill);

  if (showEdges) {
    const edges = new LineSegments(
      createAlternatingBoxEdgeGeometry(),
      new LineBasicMaterial({
        depthWrite: false,
        linewidth: 2,
        opacity: cargoCellHighlightEdgeOpacity,
        transparent: true,
        vertexColors: true
      })
    );

    edges.name = `${name}-edges`;
    edges.renderOrder = renderOrder + 1;
    highlight.add(edges);
  }

  highlight.visible = false;
  return highlight;
}

function createAlternatingBoxEdgeGeometry() {
  const positions: number[] = [];
  const colors: number[] = [];
  const boxEdges = [
    [
      [-0.5, -0.5, -0.5],
      [0.5, -0.5, -0.5]
    ],
    [
      [0.5, -0.5, -0.5],
      [0.5, 0.5, -0.5]
    ],
    [
      [0.5, 0.5, -0.5],
      [-0.5, 0.5, -0.5]
    ],
    [
      [-0.5, 0.5, -0.5],
      [-0.5, -0.5, -0.5]
    ],
    [
      [-0.5, -0.5, 0.5],
      [0.5, -0.5, 0.5]
    ],
    [
      [0.5, -0.5, 0.5],
      [0.5, 0.5, 0.5]
    ],
    [
      [0.5, 0.5, 0.5],
      [-0.5, 0.5, 0.5]
    ],
    [
      [-0.5, 0.5, 0.5],
      [-0.5, -0.5, 0.5]
    ],
    [
      [-0.5, -0.5, -0.5],
      [-0.5, -0.5, 0.5]
    ],
    [
      [0.5, -0.5, -0.5],
      [0.5, -0.5, 0.5]
    ],
    [
      [0.5, 0.5, -0.5],
      [0.5, 0.5, 0.5]
    ],
    [
      [-0.5, 0.5, -0.5],
      [-0.5, 0.5, 0.5]
    ]
  ];

  boxEdges.forEach(([start, end]) => {
    for (let index = 0; index < cargoCellHighlightEdgeDashCount; index += 1) {
      const segmentStart = index / cargoCellHighlightEdgeDashCount;
      const segmentEnd = (index + 1) / cargoCellHighlightEdgeDashCount;
      const color =
        index % 2 === 0 ? cargoCellHighlightEdgeLightColor : cargoCellHighlightEdgeDarkColor;

      positions.push(...interpolateLinePoint(start, end, segmentStart));
      positions.push(...interpolateLinePoint(start, end, segmentEnd));
      colors.push(...color, ...color);
    }
  });

  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new Float32BufferAttribute(colors, 3));
  return geometry;
}

function interpolateLinePoint(start: number[], end: number[], progress: number) {
  return [
    start[0] + (end[0] - start[0]) * progress,
    start[1] + (end[1] - start[1]) * progress,
    start[2] + (end[2] - start[2]) * progress
  ];
}

function createColumnBraceRoot(name: string, ...sideBraces: RackShelfSideBrace[]) {
  const root = new Group();
  root.name = name;
  root.add(...sideBraces);
  return root;
}

function setClipBoxPlanes(
  planes: Plane[],
  shelf: RackShelf,
  sideX: number,
  height: number,
  width: number
) {
  const minX = sideX - columnClipAreaXHalfLength;
  const maxX = sideX + columnClipAreaXHalfLength;

  setClipPlane(planes[0], shelf, new Vector3(1, 0, 0), new Vector3(minX, 0, 0));
  setClipPlane(planes[1], shelf, new Vector3(-1, 0, 0), new Vector3(maxX, 0, 0));
  setClipPlane(planes[2], shelf, new Vector3(0, 1, 0), new Vector3(0, 0, 0));
  setClipPlane(planes[3], shelf, new Vector3(0, -1, 0), new Vector3(0, height, 0));
  setClipPlane(planes[4], shelf, new Vector3(0, 0, 1), new Vector3(0, 0, 0));
  setClipPlane(planes[5], shelf, new Vector3(0, 0, -1), new Vector3(0, 0, width));
}

function setClipPlane(plane: Plane, shelf: RackShelf, localNormal: Vector3, localPoint: Vector3) {
  const worldNormal = localNormal.clone().transformDirection(shelf.matrixWorld);
  const worldPoint = localPoint.clone().applyMatrix4(shelf.matrixWorld);
  plane.setFromNormalAndCoplanarPoint(worldNormal, worldPoint);
}

function createPostXPositions(totalLength: number, lengthCellCount: number) {
  const cellLength = totalLength / lengthCellCount;

  return Array.from({ length: lengthCellCount + 1 }, (_, index) => cellLength * index);
}

function createColumnDiagonalBraceLayout(
  bottomBraceY: number,
  topBraceY: number,
  width: number
): RackShelfColumnDiagonalBraceLayout | undefined {
  const height = topBraceY - bottomBraceY;

  if (height <= 0 || width <= 0) {
    return undefined;
  }

  const minCount = Math.max(1, Math.ceil(height / (Math.tan(columnDiagonalBraceMaxAngle) * width)));
  const maxCount = Math.floor(height / (Math.tan(columnDiagonalBraceMinAngle) * width));

  if (maxCount < minCount) {
    return undefined;
  }

  const count = maxCount;
  const segmentHeight = height / count;
  const angle = Math.atan2(segmentHeight, width);

  return { angle, count, segmentHeight };
}
